import React, { useState, createContext, useContext } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { AppBar, Toolbar, Drawer, List, ListItem, ListItemText, Typography, CssBaseline, Box, IconButton, ListItemAvatar, Avatar, Divider, CircularProgress, useMediaQuery } from '@mui/material';
import { useAppBar } from '../providers/AppBarProvider';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import BottomSheet from '../layouts/BottomSheet';
import { useConfig } from '../providers/ConfigProvider';
import StoreIcon from "@mui/icons-material/Store";
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import useNavigationHelperStore from '../store/navigationHelperStore';

// Contexto para el offset del panel derecho
const RightPanelContext = createContext({ offsetRight: 0 });
export const useRightPanel = () => useContext(RightPanelContext);

import AddPet from '../assets/ic_add_pet_2.svg?react';
import SearchPet from '../assets/ic_search_pet_2.svg?react';
import AddOwner from '../assets/ic_add_person_2.svg?react';
import SearchOwner from '../assets/ic_search_owner_2.svg?react';
import SearchProduct from '../assets/ic_search_product.svg?react';
import AddSpending from '../assets/ic_add_spending_2.svg?react';
import AddProduct from '../assets/ic_add_product_2.svg?react';
import AddService from '../assets/ic_add_service_2.svg?react';
import AddProvider from '../assets/ic_add_provider_2.svg?react';
import SearchProvider from '../assets/ic_search_provider.svg?react';
import SearchManufacturer from '../assets/ic_search_manufacturer.svg?react';

import AddTask from '@mui/icons-material/AddTask';
import ShoppingCartOutlined from '@mui/icons-material/ShoppingCartOutlined';
import PaymentsOutlined from '@mui/icons-material/PaymentsOutlined';
import ListAltOutlined from '@mui/icons-material/ListAltOutlined';
import CompareArrowsOutlined from '@mui/icons-material/CompareArrowsOutlined';
import ArrowRightAltOutlined from '@mui/icons-material/ArrowRightAltOutlined';
import SmartToyOutlined from '@mui/icons-material/SmartToyOutlined';
import LanguageIcon from '@mui/icons-material/Language';

import useSellStore from '../store/sellStore';
import usePurchaseStore from '../store/purchaseStore';
import useAgendaStore from '../store/agendaStore';
import useOwnerStore from '../store/ownerStore';
import usePetStore from '../store/petStore';
import AssistantPanel from '../components/AssistantPanel';
import { useTranslation } from 'react-i18next';

const drawerWidth = 240;
//const drawerWidth = 280;

function Main() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setNavigation, resetNavigation } = useNavigationHelperStore();
  const { resetSell } = useSellStore();
  const { resetPurchase } = usePurchaseStore();
  const { resetAgenda } = useAgendaStore();
  const { resetOwner } = useOwnerStore();
  const { resetPet } = usePetStore();
  const isSmallScreen = useMediaQuery('(max-width:900px)');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const assistantWidth = 360;
  
  const { config, isLoadingConfig, error } = useConfig();
  const { t, i18n } = useTranslation();

  const languageLabels = { es: 'Español', en: 'English', pt: 'Português' };
  const languageOrder = ['es', 'en', 'pt'];
  const handleLanguageCycle = () => {
    const currentIndex = languageOrder.indexOf(i18n.language);
    const nextLang = languageOrder[(currentIndex + 1) % languageOrder.length];
    i18n.changeLanguage(nextLang);
  };
  const { thumbUrl, title, subtitle, showCloseIcon, fallbackIcon, showFab, onThumbnailClick } = useAppBar();

  const handleCloseClick = () => {
    navigate(-1);
  };

  const handleNewPet = () => {
    resetPet();
    navigate('/main/edit-pet');
  };

  const handleSearchPet = () => {
    navigate('/main/search-pet');
  };

  const handleNewOwner = () => {
    resetOwner();
    navigate('/main/edit-owner');
  };

  const handleSearchOwner = () => {
    navigate('/main/search-owner');
  };

  const handleSearchProduct = () => {
    navigate('/main/search-product');
  };

  const handleSell = () => {
    resetSell();
    setNavigation(location.pathname, {});
    navigate('/main/edit-sell');
  };

  const handleSpending = () => {
    navigate('/main/edit-spending');
  };

  const handleAgenda = () => {
    resetAgenda();
    setNavigation(location.pathname, {});
    navigate('/main/edit-agenda');
  };

  const handlePurchase = () => {
    resetPurchase();
    setNavigation(location.pathname, {});
    navigate('/main/edit-purchase');
  };

  const handleManualCash = () => {
    navigate('/main/edit-manual-cash');
  };

  const handleImportProducts = () => {
    navigate('/main/edit-import');
  };

  const handleAssociateProducts = () => {
    navigate('/main/edit-product-assoc');
  };

  const handleNewProduct = () => {
    navigate('/main/edit-product');
  };

  const handleNewService = () => {
    navigate('/main/edit-service');
  };

  const handleNewProvider = () => {
    navigate('/main/edit-provider');
  };

  const handleSearchProvider = () => {
    navigate('/main/search-provider');
  };

  const handleSearchManufacturer = () => {
    navigate('/main/search-manufacturer');
  };

  const handleMovement = () => {
    navigate('/main/edit-movement');
  };
    
  if (isLoadingConfig || !config) return (
    <>
      <CircularProgress
        size={42}
        sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-21px', marginLeft: '-21px' }}
      />
    </>
  );

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleAssistantToggle = () => {
    setAssistantOpen(!assistantOpen);
  };

  const handleDrawerNavigate = () => {
    if (isSmallScreen && mobileOpen) {
      setMobileOpen(false);
    }
  };

  const drawerContent = (
    <>
      <Toolbar />

      <Box sx={{ flex: 1 }}>
        <List>
          <ListItem button="true" component={Link} to="view-vet" onClick={handleDrawerNavigate}>
            <ListItemAvatar>
              <Avatar alt="Vet Avatar" src={config?.vet.thumb_url || undefined}>
                {!config?.vet.thumb_url && <StoreIcon />}
              </Avatar>
            </ListItemAvatar>
            <ListItemText 
              primary={config?.vet.name}
              secondary={config?.vet.email}
              secondaryTypographyProps={{ sx: { fontSize: '0.70rem' } }}
            />
            {config?.multivet > 1 && (
              <IconButton 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate('/main/view-vet-change');
                  if (isSmallScreen) setMobileOpen(false);
                }}
                size="small"
                sx={{ ml: 1 }}
              >
                <SwapHorizIcon />
              </IconButton>
            )}
          </ListItem>
          <ListItem button="true" component={Link} to="view-user" onClick={handleDrawerNavigate}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ListItemAvatar>
                <Avatar alt="User Avatar" src={config?.thumb_url} />
              </ListItemAvatar>
              <ListItemText 
                primary={config?.name}
                secondary={config?.email}
                secondaryTypographyProps={{ sx: { fontSize: '0.70rem' } }}
              />
            </Box>
          </ListItem>

          <Divider />

          <ListItem button="true" component={Link} to="home" selected={location.pathname === '/main/home'} onClick={handleDrawerNavigate}>
            <ListItemText primary={t('main.home')} />
          </ListItem>
          <ListItem button="true" component={Link} to="pets" selected={location.pathname === '/main/pets'} onClick={handleDrawerNavigate}>
            <ListItemText primary={config.vet.pet_naming_es_plural} />
          </ListItem>
          <ListItem button="true" component={Link} to="owners" selected={location.pathname === '/main/owners'} onClick={handleDrawerNavigate}>
            <ListItemText primary={config.vet.owner_naming_es_plural} />
          </ListItem>
          <ListItem button="true" component={Link} to="agenda" selected={location.pathname === '/main/agenda'} onClick={handleDrawerNavigate}>
            {/* <ListItemText primary="Agenda" /> */}
            <ListItemText primary={config.rol === "USER" ? t('main.personalAgenda') : t('main.agenda')} />
          </ListItem>

          <Divider />

          <ListItem button="true" component={Link} to="daily-cash" selected={location.pathname === '/main/daily-cash'} onClick={handleDrawerNavigate}>
            <ListItemText primary={config.rol === "USER" ? t('main.personalDailyCash') : t('main.dailyCash')} />
          </ListItem>
          <ListItem 
            button="true" 
            component={Link} 
            to="reports" 
            selected={location.pathname === '/main/reports'}
            onClick={() => { resetNavigation(); handleDrawerNavigate(); }}
          >
            <ListItemText primary={t('main.reports')} />
          </ListItem>
        </List>
      </Box>

      <Divider />

      <ListItem
        button="true"
        onClick={handleLanguageCycle}
        sx={{ cursor: 'pointer' }}
      >
        <LanguageIcon sx={{ mr: 2, color: 'action.active' }} />
        <ListItemText
          primary={languageLabels[i18n.language] || 'Español'}
          primaryTypographyProps={{ sx: { cursor: 'pointer', userSelect: 'none' } }}
        />
      </ListItem>

      <ListItem button="true" component={Link} to="about" selected={location.pathname === '/main/about'} onClick={handleDrawerNavigate}>
        <ListItemText primary={t('main.aboutDoctorVet')} />
      </ListItem>
    </>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />

      {/* AppBar */}
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          {isSmallScreen && (
            <IconButton 
              color="inherit"
              aria-label={t('common.openDrawer')}
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          {!isSmallScreen && (
            <Typography variant="h6" noWrap component="div">
              {t('common.doctorVet')}
            </Typography>
          )}
          <Box
            sx={{
              ml: isSmallScreen ? 0 : `${172}px`,
              //ml: `${201}px`,
              display: title && title.length > 0 ? 'flex' : 'none',
              alignItems: 'center',
            }}
          >
            
            {(thumbUrl != '' && thumbUrl != undefined) &&
              <Avatar
                src={thumbUrl}
                alt="Avatar section"
                sx={{ 
                  width: 50, 
                  height: 50, 
                  marginRight: '16px',
                  cursor: onThumbnailClick ? 'pointer' : 'default'
                }}
                onClick={onThumbnailClick || undefined}
              />
            }

            {(thumbUrl == '' || thumbUrl == undefined) && fallbackIcon != null &&
              <Avatar
                alt="Avatar section"
                sx={{ 
                  width: 50, 
                  height: 50, 
                  marginRight: '16px',
                  cursor: onThumbnailClick ? 'pointer' : 'default'
                }}
                onClick={onThumbnailClick || undefined}
              >
                {React.createElement(fallbackIcon, { sx: { fontSize: 32 } })}
              </Avatar>
            }

            <Box>
              <Typography variant="h6" noWrap>
                {title || ''}
              </Typography>
              <Typography variant="subtitle2" noWrap>
                {subtitle || ''}
              </Typography>
            </Box>
          </Box>

          {/* Espaciador flexible para empujar el botón de cierre hacia la derecha */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Botón para abrir el asistente en mobile */}
          {isSmallScreen && (
            <IconButton 
              color="inherit" 
              onClick={handleAssistantToggle} 
              edge="end"
              sx={{ mr: showCloseIcon ? 1 : 0 }}
              aria-label={t('main.openAssistant')}
            >
              <SmartToyOutlined />
            </IconButton>
          )}

          {/* Botón de cierre (X) en el extremo derecho */}
          {showCloseIcon && (
            <IconButton 
              color="inherit" 
              onClick={handleCloseClick} 
              edge="end"
            >
              <CloseIcon />
            </IconButton>
          )}

        </Toolbar>
      </AppBar>

      {/* Drawer */}
      {isSmallScreen ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'block' },
            [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box' },
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box' },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      <Box
        component="main"
        sx={{ flexGrow: 1, padding: 3, margin: 0, minWidth: 0, overflowX: 'hidden' }}
      >
        <Toolbar />
        <RightPanelContext.Provider value={{ offsetRight: isSmallScreen ? 0 : assistantWidth }}>
          <Outlet />
          {showFab && (!isSmallScreen || !assistantOpen) && (
            <BottomSheet 
              zIndex={1000}
              buttonGroups={[
              {
                label: t('main.mostUsed'),
                buttons: [
                  ...(config.permissions.actions_permissions.pets.create ? [{
                    label: t('main.newPet', { name: config.vet.pet_naming_es }),
                    action: handleNewPet,
                    icon: <AddPet width={42} height={42} />                    
                  }] : []),
                  ...(config.permissions.actions_permissions.pets.read ? [{
                    label: t('main.searchPet', { name: config.vet.pet_naming_es }),
                    action: handleSearchPet,
                    icon: <SearchPet width={42} height={42} />
                  }] : []),
                  ...(config.permissions.actions_permissions.owners.create ? [{
                    label: t('main.newOwner', { name: config.vet.owner_naming_es }),
                    action: handleNewOwner,
                    icon: <AddOwner width={42} height={42} />
                  }] : []),
                  ...(config.permissions.actions_permissions.owners.read ? [{
                    label: t('main.searchOwner', { name: config.vet.owner_naming_es }),
                    action: handleSearchOwner,
                    icon: <SearchOwner width={42} height={42} />
                  }] : []),
                  ...(config.permissions.actions_permissions.products.read ? [{
                    label: t('main.searchProduct'),
                    action: handleSearchProduct,
                    icon: <SearchProduct width={42} height={42} />
                  }] : []),
                  ...(config.permissions.actions_permissions.sells.create ? [{
                    label: t('main.sell'),
                    action: handleSell,
                    icon: <PointOfSaleIcon />
                  }] : []),
                  ...(config.permissions.actions_permissions.spendings.create ? [{
                    label: t('main.spending'),
                    action: handleSpending,
                    icon: <AddSpending width={32} height={32} />
                  }] : []),
                  ...(config.permissions.actions_permissions.agenda.create ? [{
                    label: t('main.agenda'),
                    action: handleAgenda,
                    icon: <AddTask />
                  }] : []),
                ].filter(Boolean)
              },
              {
                label: t('main.others'),
                buttons: [
                  ...(config.permissions.actions_permissions.purchases.create ? [{
                    label: t('main.purchase'),
                    action: handlePurchase,
                    icon: <ShoppingCartOutlined />
                  }] : []),
                  ...(config.permissions.actions_permissions.cash_movements.create ? [{
                    label: t('main.manualCash'),
                    action: handleManualCash,
                    icon: <PaymentsOutlined />
                  }] : []),
                  ...(config.permissions.actions_permissions.products.create ? [
                    {
                      label: t('main.importProducts'),
                      action: handleImportProducts,
                      icon: <ListAltOutlined />
                    },
                  ] : []),
                  ...(config.permissions.actions_permissions.products.create ? [
                    {
                      label: t('main.associateProducts'),
                      action: handleAssociateProducts,
                      icon: <CompareArrowsOutlined />
                    },
                  ] : []),
                  ...(config.permissions.actions_permissions.products.create ? [
                    {
                      label: t('main.newProduct'),
                      action: handleNewProduct,
                      icon: <AddProduct width={42} height={42} />
                    },
                  ] : []),
                  ...(config.permissions.actions_permissions.products.create ? [
                    {
                      label: t('main.newService'),
                      action: handleNewService,
                      icon: <AddService width={42} height={42} />
                    },
                  ] : []),
                  ...(config.permissions.actions_permissions.products_providers.create ? [{
                    label: t('main.newProvider'),
                    action: handleNewProvider,
                    icon: <AddProvider width={42} height={42} />
                  }] : []),
                  ...(config.permissions.actions_permissions.products_providers.read ? [{
                    label: t('main.searchProvider'),
                    action: handleSearchProvider,
                    icon: <SearchProvider width={42} height={42} />
                  }] : []),
                  ...(config.permissions.actions_permissions.products_manufacturers.read ? [{
                    label: t('main.searchManufacturer'),
                    action: handleSearchManufacturer,
                    icon: <SearchManufacturer width={42} height={42} />
                  }] : []),
                  ...(config.permissions.actions_permissions.internal_movements.create ? [{
                    label: t('main.remittance'),
                    action: handleMovement,
                    icon: <ArrowRightAltOutlined />
                  }] : []),
                ].filter(Boolean)
              }
            ]}
          />
          )}
        </RightPanelContext.Provider>
      </Box>

      {/* Drawer derecho: asistente IA */}
      {isSmallScreen ? (
        <Drawer
          anchor="right"
          variant="temporary"
          open={assistantOpen}
          onClose={handleAssistantToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'block' },
            [`& .MuiDrawer-paper`]: { width: assistantWidth, boxSizing: 'border-box' },
          }}
        >
          <Toolbar />
          <AssistantPanel />
        </Drawer>
      ) : (
        <Drawer
          anchor="right"
          variant="permanent"
          sx={{
            width: assistantWidth,
            flexShrink: 0,
            [`& .MuiDrawer-paper`]: { width: assistantWidth, boxSizing: 'border-box' },
          }}
        >
          <Toolbar />
          <AssistantPanel />
        </Drawer>
      )}

    </Box>
  );
}

export default Main;
